import * as pdfMakeModule from 'pdfmake/build/pdfmake'
import * as pdfFontsModule from 'pdfmake/build/vfs_fonts'
import { markdownToPdfmake, stripMarkdown } from './pdfMarkdown.mjs'

const pdf = pdfMakeModule?.default ?? pdfMakeModule
const vfs =
  pdfFontsModule?.pdfMake?.vfs ||
  pdfFontsModule?.default?.pdfMake?.vfs ||
  pdfFontsModule?.default?.vfs ||
  pdfFontsModule?.vfs

if (vfs) pdf.vfs = vfs

pdf.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
}

async function imageUrlToDataUrl(url) {
  if (!url) return null
  try {
    const response = await fetch(url)
    if (!response.ok) return null
    const blob = await response.blob()
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (_) {
    return null
  }
}

function nodesById(nodes) {
  return new Map(nodes.map((n) => [n.id, n]))
}

/**
 * Walk the tree from `currentNodeId` back to the root.
 */
function buildBranchChain(nodes, currentNodeId) {
  if (!currentNodeId) return []
  const byId = nodesById(nodes)
  const chain = []
  let cursor = byId.get(currentNodeId)
  while (cursor) {
    chain.unshift(cursor)
    cursor = cursor.parentNodeId ? byId.get(cursor.parentNodeId) : null
  }
  return chain
}

function formatTimestamp(ts) {
  if (!ts) return ''
  try {
    return new Date(ts).toLocaleString()
  } catch (_) {
    return String(ts)
  }
}

/**
 * Generate and download a Studio branch PDF per v2 spec §13.
 *
 * @param {object} args
 * @param {Array} args.nodes — the full studio_tree as fetched from /tree
 * @param {string} args.currentNodeId — which leaf to walk back from
 * @param {string} args.userQuestion — the original `expectations` string
 * @param {string} args.evaluation — the Anforderungen evaluation text
 * @param {Array} args.suggestions — the next-step suggestion list
 * @param {string} args.locale — 'de' | 'en' (controls fixed strings)
 */
export async function exportStudioBranchPdf({
  nodes = [],
  currentNodeId = null,
  userQuestion = '',
  evaluation = '',
  suggestions = [],
  locale = 'de',
} = {}) {
  const branch = buildBranchChain(nodes, currentNodeId)
  if (branch.length === 0) {
    throw new Error('Studio PDF export requires at least one node.')
  }

  const strings = locale === 'en'
    ? {
        coverTitle: 'Studio Concept Export',
        questionLabel: 'Your question',
        date: 'Generated',
        disclaimer: 'Concept — verify with a qualified architect before any construction work.',
        anforderungenTitle: 'Requirements evaluation',
        branchTitle: 'Concept path',
        directiveLabel: 'Directive',
        archetypeLabel: 'Archetype',
        costLabel: 'Step cost',
        creditUnit: 'credits',
        timestampLabel: 'Created',
        nextStepsTitle: 'Recommended next moves',
        cumulativeTitle: 'Cumulative changes',
        rootLabel: 'Original plan',
        stepLabel: (i) => `Step ${i}`,
      }
    : {
        coverTitle: 'Studio-Konzept Export',
        questionLabel: 'Ihre Frage',
        date: 'Erstellt',
        disclaimer: 'Konzept — vor jeder Umsetzung von einer Architektin oder einem Architekten prüfen lassen.',
        anforderungenTitle: 'Anforderungsbewertung',
        branchTitle: 'Konzeptpfad',
        directiveLabel: 'Direktive',
        archetypeLabel: 'Archetyp',
        costLabel: 'Schrittkosten',
        creditUnit: 'Credits',
        timestampLabel: 'Erstellt',
        nextStepsTitle: 'Empfohlene nächste Schritte',
        cumulativeTitle: 'Bisherige Änderungen',
        rootLabel: 'Originalplan',
        stepLabel: (i) => `Schritt ${i}`,
      }

  const nodesWithImages = await Promise.all(
    branch.map(async (node) => ({
      node,
      dataUrl: await imageUrlToDataUrl(node.imageUrl),
    })),
  )

  const cover = [
    { text: strings.coverTitle, style: 'coverTitle' },
    { text: `${strings.date}: ${new Date().toLocaleString()}`, style: 'meta' },
    userQuestion
      ? { text: `${strings.questionLabel}: ${userQuestion}`, style: 'metaQuote', margin: [0, 10, 0, 0] }
      : null,
    nodesWithImages[0]?.dataUrl
      ? { image: nodesWithImages[0].dataUrl, width: 420, alignment: 'center', margin: [0, 24, 0, 18] }
      : null,
    { text: strings.disclaimer, style: 'disclaimer' },
    { text: '', pageBreak: 'after' },
  ].filter(Boolean)

  const anforderungenSection = evaluation
    ? [
        { text: strings.anforderungenTitle, style: 'h2' },
        ...markdownToPdfmake(evaluation, { headingStyle: 'h3' }),
        { text: '', pageBreak: 'after' },
      ]
    : []

  const branchPages = nodesWithImages.flatMap(({ node, dataUrl }, idx) => {
    const headingLabel = node.parentNodeId === null
      ? strings.rootLabel
      : strings.stepLabel(idx)
    const cells = [
      { text: headingLabel, style: 'h2' },
      dataUrl
        ? { image: dataUrl, width: 420, alignment: 'center', margin: [0, 10, 0, 14] }
        : { text: '—', style: 'meta', margin: [0, 10, 0, 14] },
    ]
    if (node.parentNodeId !== null) {
      cells.push({
        columns: [
          [
            { text: strings.directiveLabel, style: 'label' },
            { text: stripMarkdown(node.directive || '—'), margin: [0, 0, 0, 8] },
          ],
          [
            { text: strings.archetypeLabel, style: 'label' },
            { text: node.archetype || '—', margin: [0, 0, 0, 8] },
            { text: strings.costLabel, style: 'label', margin: [0, 6, 0, 0] },
            { text: `${node.creditCost || 0} ${strings.creditUnit}`, margin: [0, 0, 0, 8] },
            { text: strings.timestampLabel, style: 'label', margin: [0, 6, 0, 0] },
            { text: formatTimestamp(node.createdAt), margin: [0, 0, 0, 8] },
          ],
        ],
        columnGap: 16,
      })
    }
    cells.push({ text: '', pageBreak: 'after' })
    return cells
  })

  // pdfmake's `ol:` already auto-numbers; if the directive happens to start
  // with "1." (e.g. a starter-suggestion that was emitted as part of a list),
  // strip that leading marker so the output isn't "1. 1. <directive>".
  const cumulativeDirectives = branch
    .filter((node) => node.parentNodeId)
    .map((node) => stripMarkdown(node.directive || '—').replace(/^\d+\.\s+/, ''))
  const cumulativeSection = cumulativeDirectives.length
    ? [
        { text: strings.cumulativeTitle, style: 'h2' },
        { ol: cumulativeDirectives, margin: [0, 6, 0, 14] },
      ]
    : []

  // Suggestion descriptions can include markdown (bold, lists). Strip it down
  // to a clean single-line bullet so the "Empfohlene nächste Schritte" page
  // reads as a tight summary rather than a wall of `**` markers.
  const nextStepsSection = (suggestions || []).length
    ? [
        { text: strings.nextStepsTitle, style: 'h2', pageBreak: 'before' },
        {
          ul: suggestions.slice(0, 3).map((s) => {
            const title = stripMarkdown(s.title || '')
            const desc = stripMarkdown(s.description || '')
            return desc ? `${title} — ${desc}` : title
          }),
          margin: [0, 6, 0, 14],
        },
      ]
    : []

  const docDefinition = {
    content: [
      ...cover,
      ...anforderungenSection,
      { text: strings.branchTitle, style: 'h2' },
      ...branchPages,
      ...cumulativeSection,
      ...nextStepsSection,
    ],
    defaultStyle: { font: 'Roboto', fontSize: 11, lineHeight: 1.35 },
    styles: {
      coverTitle: { fontSize: 24, bold: true, margin: [0, 0, 0, 6] },
      h2: { fontSize: 16, bold: true, margin: [0, 14, 0, 8] },
      h3: { fontSize: 13, bold: true, margin: [0, 10, 0, 4], color: '#1f3a44' },
      label: { fontSize: 9, bold: true, color: '#55717c' },
      meta: { color: '#55717c' },
      metaQuote: { italics: true, color: '#55717c' },
      disclaimer: { italics: true, color: '#76532a', fontSize: 10 },
    },
    pageMargins: [40, 40, 40, 40],
  }

  const fileName = `studio-konzept-${new Date().toISOString().slice(0, 10)}.pdf`
  pdf.createPdf(docDefinition).download(fileName)
}
